var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useRef, useCallback, useMemo, useEffect, } from "react";
import { StyledTagEditor, StyledRowRenderer, StyledSuggestionsDropdown, StyledInput, StyledTagListContainer, StyledTagItem, StyledSpinnerWrapper, StyledErrorIconContainer, StyledInputRuler, StyledTagList, } from "./TagEditor.style";
import Flex from "../box/Flex";
import Spinner from "../loader/Spinner";
import useUniqueId from "../../hooks/useUniqueId";
import useRuler from "../../hooks/useRuler";
import { useNxTranslation } from "../../i18n/useNxTranslation";
import { apolloSpaceInset1X, apolloSpaceInsetHalfX } from "@nexthink/apollo-tokens";
import { StyledErrorIcon } from "../input/Input.style";
import FloatingPortalContainer from "../../hoc/FloatingPortalContainer";
import EscapeKeyBoundary from "../../utils/EscapeKeyBoundary";
import Menu from "../menu/Menu";
import MenuItem from "../menu/MenuItem";
import { highlightSuggestion } from "../menu/highlighting";
import { trapEvent } from "../../utils/EventHelpers";
import { getNormalizedIndex } from "../../utils/ArrayHelpers";
import find from "lodash/find";
import InteractiveTag from "../interactive-tag/InteractiveTag";
import TranslationEN from "./locales/translation-en.json";
import TranslationFR from "./locales/translation-fr.json";
import Box from "../box/Box";
import { StyledHiddenScreenReaderOnly } from "../../utils/StyleUtils";
import useIdAttrs from "../../hooks/useIdAttrs";
import usePortal from "../../hooks/usePortal";
import { useCalcDropdownItems } from "./useCalcDropdownItems";
const i18nNamespace = "tag-editor";
const prefixId = "nx-tag-editor-";
const allowedKeyActions = ["Enter", "Backspace", "Escape", "ArrowUp", "ArrowDown"];
const navigationDirection = { ArrowDown: 1, ArrowUp: -1 };
const descriptiveRowRenderer = (translationKey, t) => (React.createElement(Flex, { px: apolloSpaceInset1X, py: apolloSpaceInsetHalfX },
    React.createElement(StyledRowRenderer, { variant: "bodySmall" }, t(translationKey, { ns: i18nNamespace }))));
export const suggestionLoadingRenderer = () => (React.createElement(Flex, { px: apolloSpaceInset1X, py: apolloSpaceInsetHalfX, alignItems: "center", justifyContent: "center" },
    React.createElement(StyledSpinnerWrapper, null,
        React.createElement(Spinner, { id: "spinner-small", size: "body" }))));
const footerRenderer = (t) => descriptiveRowRenderer("pressEnterMsg", t);
const renderError = (id, insightsAttr, error) => {
    const errorMessage = typeof error === "string" ? error : undefined;
    return (React.createElement(StyledErrorIconContainer, Object.assign({ id: `${id}-error`, "data-insights": insightsAttr && `${insightsAttr}-error` }, (errorMessage && { "aria-live": "polite" })),
        React.createElement(Box, { as: "span", display: "inline-block", width: "100%", height: "100%", title: errorMessage, "aria-hidden": "true" },
            React.createElement(StyledErrorIcon, null)),
        errorMessage && React.createElement(StyledHiddenScreenReaderOnly, null, errorMessage)));
};
const isTagAlreadySelected = (tags, incomingTag, getter) => {
    const existingTagIndex = tags.map(getter).findIndex((elem) => elem.toUpperCase() === incomingTag.toUpperCase());
    return existingTagIndex !== -1;
};
const filterSuggestions = (selectedTags, suggestions, filter, getter) => {
    const tags = selectedTags.map(getter);
    return suggestions.filter((suggestion) => {
        const value = getter(suggestion);
        const isNotInSelectedTags = tags.findIndex((elem) => elem === value) === -1;
        const isPartOfFilter = filter === "" ? true : value.toUpperCase().includes(filter.toUpperCase());
        return isNotInSelectedTags && isPartOfFilter;
    });
};
const getPlaceholder = ({ emptyPlaceholder, placeholder }, tagList, t) => tagList.length === 0
    ? emptyPlaceholder || t("emptyPlaceholder", { ns: i18nNamespace })
    : placeholder || t("placeholder", { ns: i18nNamespace });
const TagEditor = (_a) => {
    var { id: defaultId, "data-insights": insightsAttr, tagLimit, error, placeholder, emptyPlaceholder, tags, onTagsUpdate, textField, suggestions = [], dropdownVisibleItems = "auto", allowNewTag = true, suggestionLoading = false, disablePortal = false, flip = true, container, virtualized = false, stopEnterPropagation = false, withFlags = false, disabled = false } = _a, rest = __rest(_a, ["id", "data-insights", "tagLimit", "error", "placeholder", "emptyPlaceholder", "tags", "onTagsUpdate", "textField", "suggestions", "dropdownVisibleItems", "allowNewTag", "suggestionLoading", "disablePortal", "flip", "container", "virtualized", "stopEnterPropagation", "withFlags", "disabled"]);
    const id = useUniqueId(prefixId, defaultId);
    const idAttrs = useIdAttrs({ id, insightsAttr });
    const dropdownAttrs = idAttrs("-dropdown");
    // I18n
    const { t, nxI18n } = useNxTranslation();
    nxI18n.addResources("en", i18nNamespace, TranslationEN);
    nxI18n.addResources("fr", i18nNamespace, TranslationFR);
    const tagEditorRef = useRef(null);
    const [dropdownRef, setDropdownRef] = useState(null);
    const inputRef = useRef(null);
    const getter = useCallback((item) => {
        if (typeof item === "string") {
            return item;
        }
        if (!textField || !Object.keys(item).includes(textField) || typeof item[textField] !== "string") {
            return "";
        }
        return item[textField];
    }, [textField]);
    const [filteredSuggestions, setFilteredSuggestions] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [previousInputValue, setPreviousInputValue] = useState("");
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [opened, setOpened] = useState(false);
    const hasReachedLimit = !!tagLimit && tags.length >= tagLimit;
    const isDropdownVisible = useMemo(() => opened && !hasReachedLimit, [opened, hasReachedLimit]);
    const hasAvailableSuggestions = isDropdownVisible && filteredSuggestions.length > 0;
    // controls whether menu shows focus rings
    const initialIsMenuHighlighted = !allowNewTag;
    const [isMenuHighlighted, setIsMenuHighlighted] = useState(initialIsMenuHighlighted);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const hasErrorMessage = typeof error === "string";
    // set the filtered suggestions whenever the prop `suggestions` changes
    useEffect(() => {
        setFilteredSuggestions(filterSuggestions(tags, suggestions, inputValue, getter));
    }, [isDropdownVisible, suggestions.length, tags.length, getter]);
    useEffect(() => {
        // prevents wrapper forms from submitting on enter
        const preventDefaultOnEnter = (event) => {
            if (stopEnterPropagation && event.key === "Enter") {
                event.preventDefault();
            }
        };
        document.addEventListener("keydown", preventDefaultOnEnter);
        return () => {
            document.removeEventListener("keydown", preventDefaultOnEnter);
        };
    }, [stopEnterPropagation]);
    const { getPortalProps, getTargetProps, portalStyles } = usePortal({
        onVisibleChange: (isVisible) => {
            var _a;
            if (disabled) {
                return;
            }
            setOpened(isVisible);
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        },
        visible: opened,
        portalOptions: {
            flip,
            sameWidthTarget: true,
            shouldHideWhenTargetHidden: false,
        },
        portalRef: dropdownRef,
        targetRef: tagEditorRef.current,
    });
    const handleAddTag = useCallback((tagToAdd) => {
        if (tagLimit && tags.length >= tagLimit) {
            return;
        }
        let newTags = [];
        if (typeof tagToAdd === "string") {
            const tagText = tagToAdd.trim();
            const tagExists = isTagAlreadySelected(tags, tagText, getter);
            if (tagExists || tagText === "") {
                return;
            }
            newTags = [...tags, textField ? { [textField]: tagText } : tagText];
        }
        else {
            if (find(tags, tagToAdd)) {
                return;
            }
            newTags = [...tags, tagToAdd];
        }
        onTagsUpdate(newTags);
        setInputValue("");
        filteredSuggestions && setFocusedIndex(Math.min(focusedIndex, filteredSuggestions.length - 2));
        suggestions.length && setFilteredSuggestions(filterSuggestions(newTags, suggestions, "", getter));
        setOpened(false);
    }, [
        tagLimit,
        tags.length,
        getter,
        textField,
        onTagsUpdate,
        filteredSuggestions.length,
        focusedIndex,
        suggestions.length,
    ]);
    const handleRemove = useCallback((elem) => {
        const newValue = tags.filter((tag) => {
            if (typeof tag === "string") {
                return tag !== elem;
            }
            if (!textField || !Object.keys(tag).includes(textField) || !tag[textField]) {
                return false;
            }
            return tag[textField] !== elem;
        });
        onTagsUpdate([...newValue]);
        suggestions.length > 0 && setFilteredSuggestions(filterSuggestions(newValue, suggestions, inputValue, getter));
    }, [tags.length, textField, onTagsUpdate, suggestions.length, inputValue, getter]);
    const navigateHighlight = (direction) => {
        if (!isMenuHighlighted || !opened) {
            setFocusedIndex(0);
        }
        else if (filteredSuggestions.length > 0) {
            setFocusedIndex(getNormalizedIndex(filteredSuggestions, focusedIndex + direction));
        }
        setIsMenuHighlighted(true);
    };
    const handleKeyAction = (key, value) => {
        switch (key) {
            case "Enter":
                if (isMenuHighlighted && hasAvailableSuggestions && filteredSuggestions.length > 0) {
                    handleAddTag(filteredSuggestions[focusedIndex]);
                    // if it's the last element being selected, let's focus the one above
                    setFocusedIndex(Math.min(focusedIndex, filteredSuggestions.length - 2));
                    setIsMenuHighlighted(initialIsMenuHighlighted);
                }
                else if (allowNewTag) {
                    handleAddTag(value);
                }
                return;
            case "Backspace":
                // if there's no input value, remove last tag
                if (tags.length > 0 && !previousInputValue) {
                    handleRemove(getter(tags[tags.length - 1]));
                    setOpened(false);
                }
                else if (suggestions.length > 0) {
                    setFilteredSuggestions(filterSuggestions(tags, suggestions, value, getter));
                }
                return;
            case "Escape":
                setOpened(false);
                setIsMenuHighlighted(initialIsMenuHighlighted);
                return;
            default:
                if (Object.keys(navigationDirection).includes(key)) {
                    navigateHighlight(navigationDirection[key]);
                }
        }
    };
    const handleInputKeyUp = (e) => {
        setPreviousInputValue(inputValue);
        setOpened(true);
        setFocusedIndex(0);
        const value = e.currentTarget.value;
        setInputValue(value);
        if (allowedKeyActions.includes(e.key)) {
            handleKeyAction(e.key, value);
        }
        else if (suggestions.length > 0) {
            setFilteredSuggestions(filterSuggestions(tags, suggestions, value, getter));
        }
    };
    const handleFlagUpdate = (index, newColor) => {
        const updatedTags = [...tags];
        const tag = updatedTags[index];
        if (typeof tag !== "string") {
            tag.flag = newColor;
            updatedTags[index] = tag;
            onTagsUpdate(updatedTags);
        }
    };
    const handleInputChange = useCallback((e) => {
        setInputValue(e.target.value);
        suggestions.length > 0 && setFilteredSuggestions(filterSuggestions(tags, suggestions, e.target.value, getter));
    }, [tags.length, suggestions.length, getter]);
    const renderDropdownFooter = useCallback(() => (React.createElement(React.Fragment, null,
        suggestionLoading
            ? suggestionLoadingRenderer()
            : !hasAvailableSuggestions && inputValue !== "" && descriptiveRowRenderer("noSuggestions", t),
        allowNewTag && inputValue !== "" && footerRenderer(t))), [suggestionLoading, hasAvailableSuggestions, inputValue, allowNewTag]);
    const { visibleItems } = useCalcDropdownItems({
        dropdownVisibleItems,
        visible: opened,
        size: "small",
        suggestions,
        filteredSuggestions,
        allowNewTag,
        inputValue,
    });
    const renderTags = useCallback(() => tags.length > 0 && (React.createElement(StyledTagList, null, tags.map((tag, index) => {
        const value = getter(tag);
        const tagError = (typeof tag !== "string" && tag.error) || undefined;
        let tagColorFlag = undefined;
        if (typeof tag !== "string" && withFlags) {
            tagColorFlag = tag.flag ? tag.flag : "none";
        }
        return (React.createElement(StyledTagItem, { key: value, onClick: (e) => trapEvent(e) },
            React.createElement(InteractiveTag, Object.assign({ error: tagError, flag: tagColorFlag, disabled: disabled, text: value, onRemove: () => {
                    var _a;
                    handleRemove(value);
                    (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                } }, (withFlags && { onFlagUpdate: (newColor) => handleFlagUpdate(index, newColor) })))));
    }))), [tags.length, getter, handleRemove, disabled]);
    const suggestionsDropdown = (React.createElement(StyledSuggestionsDropdown, Object.assign({ ref: setDropdownRef }, dropdownAttrs, { style: portalStyles }, getPortalProps()),
        React.createElement(Menu, { id: `${id}-suggestions-menu`, "data-insights": insightsAttr && `${insightsAttr}-suggestions-menu`, "aria-label": "suggestions menu", variant: "select", size: "small", isSelectable: false, focusedIndex: isMenuHighlighted ? focusedIndex : -1, drawFocusRingWithoutFocusing: true, menuVisibleItems: visibleItems, virtualized: virtualized }, filteredSuggestions.map((suggestion, i) => {
            const label = getter(suggestion);
            return (React.createElement(MenuItem, { id: `${id}-suggestions-menu-${i}`, key: label, onClick: () => {
                    handleAddTag(suggestion);
                } }, highlightSuggestion(label, inputValue)));
        })),
        renderDropdownFooter()));
    const renderDropdown = () => (React.createElement(FloatingPortalContainer, { container: container, content: suggestionsDropdown, disablePortal: disablePortal }));
    // If one of the tags is in error, then the tag editor has to be in error too
    const isAnyTagInError = () => {
        return tags.some((tag) => {
            return typeof tag !== "string" && !!tag.error;
        });
    };
    const placeholderText = getPlaceholder({ placeholder, emptyPlaceholder }, tags, t);
    // this element is used to measure the width of the text, either the placeholder or input value
    const { rect: inputRect, ref: rulerRef } = useRuler();
    return (React.createElement(Flex, Object.assign({ ref: tagEditorRef, id: id, "data-insights": insightsAttr, alignItems: "center", width: "100%", position: "relative", onClick: trapEvent, onMouseDown: trapEvent }, rest),
        React.createElement(StyledTagEditor, { error: isAnyTagInError() || error, isInputFocused: isInputFocused, disabled: disabled, onClick: getTargetProps().onClick },
            React.createElement(StyledTagListContainer, { "data-insights": insightsAttr && `${insightsAttr}-tags` },
                renderTags(),
                React.createElement(EscapeKeyBoundary, { disabled: !opened },
                    React.createElement(StyledInputRuler, { ref: rulerRef, isPlaceholder: !inputValue }, inputValue || placeholderText),
                    React.createElement(StyledInput, Object.assign({ key: "input", role: "combobox", ref: inputRef }, idAttrs("-input"), { width: inputRect === null || inputRect === void 0 ? void 0 : inputRect.width, "aria-controls": dropdownAttrs.id, "aria-haspopup": "listbox", "aria-expanded": opened, "aria-activedescendant": opened ? `${id}-suggestions-menu-${focusedIndex}` : undefined, disabled: disabled, autoComplete: "off", value: inputValue, placeholder: placeholderText, hasReachedLimit: hasReachedLimit, onKeyUp: handleInputKeyUp, onChange: handleInputChange, onFocus: () => setIsInputFocused(true), onBlur: () => {
                            setIsInputFocused(false);
                            setInputValue("");
                            setOpened(false);
                        }, "aria-label": placeholderText }, (error && Object.assign({ "aria-invalid": true }, (hasErrorMessage && { "aria-errormessage": `${id}-error` })))))))),
        isDropdownVisible && renderDropdown(),
        (isAnyTagInError() || error) && renderError(id, insightsAttr, error)));
};
export default TagEditor;
//# sourceMappingURL=TagEditor.js.map